import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  LogOut, Heart, Trophy, Share2, Copy, ExternalLink, Sparkles, Users, Target,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Brand } from "@/components/Brand";
import { ProgressWave } from "@/components/ProgressWave";
import { LeaderboardTable } from "@/components/LeaderboardTable";
import { celebrate } from "@/components/celebrate";
import { useAuth } from "@/context/AuthContext";
import { api, money } from "@/lib/api";

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-2xl border border-[#e4d8c4] bg-white p-5">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[#7c7a6a]">
        <Icon className="h-4 w-4" style={{ color: accent }} /> {label}
      </div>
      <div className="mt-2 font-heading text-3xl font-extrabold" style={{ color: accent }}>{value}</div>
    </div>
  );
}

export default function SupporterDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get("/me/dashboard").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (data?.team?.pct >= 100) {
      const t = setTimeout(celebrate, 400);
      return () => clearTimeout(t);
    }
  }, [data?.team?.pct]);

  const doLogout = () => { logout(); navigate("/"); };

  if (!data) {
    return (
      <div className="grid min-h-screen place-items-center bg-[#f8f3e9]">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#e4d8c4] border-t-[#65949c]" />
      </div>
    );
  }

  const { team, campaign, my_raised, team_rank, leaderboard, givebutter_url } = data;
  const sharePct = team?.raised ? Math.round((my_raised / team.raised) * 100) : 0;

  const shareMsg = `I'm fundraising with ${team?.name || "our team"} for Hope Floats — helping local kids learn to swim through the Little Splash Swim Program. Please chip in here: ${givebutter_url}`;

  const copy = (text, msg) => {
    navigator.clipboard.writeText(text);
    toast.success(msg);
  };

  return (
    <div className="min-h-screen bg-[#f8f3e9]">
      <header className="sticky top-0 z-40 hf-glass border-b border-[#e4d8c4]/70">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3">
          <Brand />
          <div className="flex items-center gap-3">
            <span className="hidden text-sm font-semibold text-[#3f6670] sm:inline">Hi, {user.name}</span>
            <Button variant="outline" size="sm" onClick={doLogout} className="rounded-full border-[#e4d8c4] text-[#3f6670]" data-testid="supporter-logout-button">
              <LogOut className="mr-1.5 h-4 w-4" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-8">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <span className="hf-eyebrow">My fundraising</span>
          <h1 className="mt-1 font-heading text-3xl font-extrabold text-[#00334a]">
            {team ? `Team ${team.name}` : "Your dashboard"}
          </h1>
          {campaign && <p className="mt-1 text-[#7c7a6a]">{campaign.name}</p>}
        </motion.div>

        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          <Stat icon={Heart} label="You've raised" value={money(my_raised)} accent="#c97049" />
          <Stat icon={Trophy} label="Team rank" value={team_rank ? `#${team_rank}` : "—"} accent="#c99a4e" />
          <Stat icon={Sparkles} label="Your share" value={`${sharePct}%`} accent="#87856c" />
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            {/* Team progress */}
            {team && (
              <div className="rounded-3xl border border-[#e4d8c4] bg-white p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 font-heading text-lg font-bold text-[#00334a]">
                    <Target className="h-5 w-5 text-[#65949c]" /> Team goal progress
                  </div>
                  <span className="font-heading text-lg font-extrabold text-[#65949c]">{team.pct}%</span>
                </div>
                <div className="mt-3"><ProgressWave pct={team.pct} height="h-5" showMilestones /></div>
                <div className="mt-3 flex justify-between text-sm font-semibold text-[#7c7a6a]">
                  <span>{money(team.raised)} raised</span>
                  <span>Goal {money(team.goal)}</span>
                </div>
                <div className="mt-4 flex items-center gap-1.5 text-xs text-[#7c7a6a]">
                  <Users className="h-4 w-4" /> {team.member_count} teammates raising together
                </div>
              </div>
            )}

            {/* Givebutter donate */}
            <div className="overflow-hidden rounded-3xl border border-[#e4d8c4] bg-white" data-testid="givebutter-embed-container">
              <div className="hf-gradient-deep p-6 text-white">
                <h2 className="font-heading text-xl font-bold">Give or ask friends to give</h2>
                <p className="mt-1 text-sm text-sky-100">All donations are securely processed on Givebutter and count toward your team.</p>
              </div>
              <div className="p-6">
                <div className="rounded-xl border border-[#e4d8c4] bg-[#f8f3e9] p-6 text-center">
                  <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-white shadow-sm">
                    <Heart className="h-6 w-6 text-[#c97049]" />
                  </div>
                  <p className="mt-3 font-heading text-xl font-semibold text-[#00334a]">Give securely on Givebutter</p>
                  <p className="mt-1 text-sm text-[#7c7a6a]">
                    Donations are processed by Givebutter and count toward your team's total. Every $300–$400
                    covers a full session of swim lessons for one local child.
                  </p>
                </div>
                <a href={givebutter_url} target="_blank" rel="noopener noreferrer" className="mt-4 block" data-testid="supporter-donate-button">
                  <Button className="w-full rounded-full hf-btn-coral py-6 text-base font-bold">
                    Open donation page <ExternalLink className="ml-2 h-4 w-4" />
                  </Button>
                </a>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {/* Recruit toolkit */}
            <div className="rounded-3xl border border-[#e4d8c4] bg-white p-6">
              <div className="flex items-center gap-2 font-heading text-lg font-bold text-[#00334a]">
                <Share2 className="h-5 w-5 text-[#c97049]" /> Recruit & share
              </div>
              <p className="mt-2 text-sm text-[#7c7a6a]">Share your team message with friends and family to grow your impact.</p>
              <div className="mt-4 rounded-xl bg-[#f8f3e9] p-3 text-xs text-[#5a5849]">{shareMsg}</div>
              <Button onClick={() => copy(shareMsg, "Share message copied!")} className="mt-3 w-full rounded-full bg-[#00334a] font-bold hover:bg-[#00334a]" data-testid="supporter-copy-share-message">
                <Copy className="mr-2 h-4 w-4" /> Copy share message
              </Button>
              <Button variant="outline" onClick={() => copy(givebutter_url, "Donation link copied!")} className="mt-2 w-full rounded-full border-[#e4d8c4] text-[#3f6670]" data-testid="supporter-copy-invite-link">
                Copy donation link
              </Button>
            </div>

            {/* Leaderboard */}
            <div>
              <div className="mb-3 flex items-center gap-2 font-heading text-lg font-bold text-[#00334a]">
                <Trophy className="h-5 w-5 text-[#c99a4e]" /> Leaderboard
              </div>
              <LeaderboardTable rows={leaderboard} highlightTeamId={team?.id} />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
